import React from 'react';
import { fmtDate, fmtDateTime, usd } from '@/lib/billingApi';
import { statusText } from './shared';

const Fact = ({ label, value, mono, testId, wide }) => (
  <div className={`min-w-0 ${wide ? 'col-span-2' : ''}`}>
    <p className="text-[11px] uppercase tracking-wide font-bold text-slate-400">{label}</p>
    <p className={`text-sm text-slate-800 break-all ${mono ? 'font-mono' : 'font-medium'}`} data-testid={testId}>{value || '—'}</p>
  </div>
);

export const TxnFacts = ({ p, receipt, sub }) => (
  <div className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="txn-facts">
    <Fact label="Amount" value={`${usd(p.total)} ${p.currency || ''}`} testId="txn-amount" />
    <Fact label="Refunded" value={p.refunded_amount ? usd(p.refunded_amount) : ''} testId="txn-refunded" />
    <Fact label="Customer" value={p.user_name ? `${p.user_name} · ${p.user_email}` : p.user_email} wide />
    <Fact label="Payment method" value={p.method} />
    <Fact label="Auto-renew" value={p.auto_renew ? 'On' : 'Off'} />
    <Fact label="Order ID" value={p.order_id} mono testId="txn-order-id" />
    <Fact label="Gateway ref" value={p.gateway_ref} mono />
    <Fact label="Auth code" value={p.auth_code} mono />
    <Fact label="Verified by" value={statusText(p.verification_method)} />
    <Fact label="Created" value={fmtDateTime(p.created_at)} />
    <Fact label="Paid" value={p.paid_at ? fmtDateTime(p.paid_at) : ''} />
    {!p.parts && <Fact label="Service period" value={p.period_start ? `${fmtDate(p.period_start)} → ${fmtDate(p.period_end)}` : ''} />}
    <Fact label="Subscription" value={sub ? `${statusText(sub.status)}${sub.auto_renew ? '' : ' · auto-renew off'}` : ''} />
    <Fact label="Receipt" value={receipt ? `${receipt.number} · email ${statusText(receipt.email_status)}` : ''} testId="txn-receipt" wide />
    {p.parts && <Fact label="Includes" value={p.parts.map((x) => `${x.short} ${usd(x.amount)}/${x.interval === 'year' ? 'yr' : 'mo'}${x.period_end ? ` → ${fmtDate(x.period_end)}` : ''}`).join(' · ')} testId="txn-parts" wide />}
    {p.coupon && <Fact label="Coupon" value={`${p.coupon.code} · ${p.coupon.percent_off}% off ${p.coupon.duration === 'forever' ? 'every renewal' : 'first payment'} (saved ${usd(p.coupon.discount)})`} testId="txn-coupon" wide />}
    {p.failure_reason && <Fact label="Failure reason" value={p.failure_reason} wide />}
  </div>
);

export const RefundList = ({ refunds }) => (!refunds.length ? null : (
  <div data-testid="txn-refunds">
    <p className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Refunds</p>
    <div className="space-y-2">
      {refunds.map((r) => (
        <div key={r.id} className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-sm">
          <div className="flex justify-between gap-2"><span className="font-bold text-violet-900">{usd(r.amount)}</span><span className="text-xs text-violet-700">{statusText(r.method)}</span></div>
          <p className="text-xs text-slate-600 mt-0.5">{r.reason}{r.reference ? ` · ref ${r.reference}` : ''}</p>
          <p className="text-[11px] text-slate-400">{fmtDateTime(r.at)} · {r.by}</p>
        </div>
      ))}
    </div>
  </div>
));

export const AuditTrail = ({ rows }) => (
  <div>
    <p className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Audit trail</p>
    {!rows.length ? <p className="text-sm text-slate-400">No events recorded.</p> : (
      <ol className="space-y-2.5" data-testid="txn-audit">
        {rows.map((a) => (
          <li key={a.id} className="flex gap-3">
            <span className="mt-1.5 w-2 h-2 rounded-full bg-[#00247D] shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-800 capitalize">{statusText(a.action)}</p>
              <p className="text-[11px] text-slate-500">{fmtDateTime(a.at)} · {a.actor}</p>
            </div>
          </li>
        ))}
      </ol>
    )}
  </div>
);
